import { useCallback } from 'react';
import { rgba } from 'polished';
import styled from 'styled-components';

import {
  c07071D,
  mainGridGapColor,
  c00EE91,
  cFF005C,
  cFFFFFF,
  c14142E,
} from '#/colors';

import { Loadable } from '#/components/shared/Loadable';

import {
  OrderBookItemView,
  useOrderBookView,
} from '#/features/perpetuals/order-book/order-book-context';
import {
  useOrderFormActions,
  useOrderFormView,
} from '#/features/perpetuals/order-builder/form-context';
import { LastTradeProvider } from '#/features/perpetuals/trades/last-trade-view-context';
import { useTranslation } from '#/features/localization/utils';

import capitalizeText from '#/utils/capitalizeText';

import VolumeIndication from './VolumeIndication';
import LastTradedPrice from './LastTradedPrice';

export default function OrderBook() {
  return <OrderBookContent />;
}

function OrderBookContent() {
  const { error, status, loadRetryCount } = useOrderBookView();

  if (status === 'error' && error.length > 0)
    return <Error data-test-load-retry-count={loadRetryCount}>{error}</Error>;

  const isLoading = status === 'loading' || status === 'disconnected';

  return (
    <Loadable isLoading={isLoading}>
      <Content />
    </Loadable>
  );
}

function Content(props: { className?: string }) {
  const { orderBook, spread, baseCurrency, loadRetryCount } =
    useOrderBookView();
  const { t } = useTranslation();

  return (
    <Container
      role="grid"
      aria-readonly="true"
      className={props.className}
      data-test-load-retry-count={loadRetryCount}
    >
      <HeaderRow>
        <span role="rowheader">{t('Price')}</span>
        <span role="rowheader">{t('Size')}</span>
        <span role="rowheader">
          {/* Separating each piece in a tag prevents the issue with Google
          Translate auto translating to Chinese on page load from happening.
          Issue: https://bugs.chromium.org/p/chromium/issues/detail?id=872770 */}
          <span>{t('Total')}(</span>
          <span>{baseCurrency}</span>
          <span>)</span>
        </span>
      </HeaderRow>

      {orderBook.asks.map((ask, idx) => (
        // Note that empty rows won't have a unique price
        // eslint-disable-next-line react/no-array-index-key
        <OrderBookRow key={`${idx}-${ask.rawPrice}`} item={ask} kind="ask" />
      ))}

      <SpreadRow>
        <LastTradeProvider>
          <LastTradedPrice />
        </LastTradeProvider>
        <span>{t('Spread')}</span>
        <span>{spread.relative}</span>
      </SpreadRow>

      {orderBook.bids.map((bid, idx) => (
        // Note that empty rows won't have a unique price
        // eslint-disable-next-line react/no-array-index-key
        <OrderBookRow key={`${idx}-${bid.rawPrice}`} item={bid} kind="bid" />
      ))}
    </Container>
  );
}

function OrderBookRow(props: {
  readonly item: OrderBookItemView;
  readonly kind: 'ask' | 'bid';
}) {
  const { item, kind } = props;
  const { updateLimitPrice } = useOrderFormActions();
  const { type: orderType } = useOrderFormView();

  const updatesOrderPrice = orderType === 'LIMIT';

  const handleClick = useCallback(() => {
    if (!updatesOrderPrice) return;
    updateLimitPrice(item.rawPrice);
  }, [updatesOrderPrice, item.rawPrice, updateLimitPrice]);

  return (
    <Row
      onClick={handleClick}
      clickable={updatesOrderPrice}
      aria-label={`${capitalizeText(kind)} @ ${item.price}`}
    >
      <Price role="gridcell" kind={kind}>
        {item.price}
      </Price>
      <span role="gridcell">{item.size}</span>
      <span role="gridcell">{item.runningSize}</span>
      <VolumeIndication
        width="70%"
        primary={item.sizeRelativePercentage}
        secondary={item.runningSizeRelativePercentage}
        side={kind === 'bid' ? 'BUY' : 'SELL'}
      />
    </Row>
  );
}

const Container = styled.div`
  position: absolute;
  min-width: 100%;
  font-size: 11px;
`;

const BaseRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  padding: 0 16px;
  align-items: center;
  height: 22px;
  color: ${rgba(cFFFFFF, 0.7)};
  font-family: 'Roboto Mono', monospace;

  & > * {
    justify-self: end;

    &:nth-child(1) {
      justify-self: flex-start;
    }
  }
`;

const HeaderRow = styled(BaseRow).attrs({ role: 'row' })`
  z-index: 1; // keeps header on top when scrolling
  position: sticky;
  top: 0;
  height: 32px;
  margin-bottom: 3px;
  border-bottom: 1px solid ${mainGridGapColor};
  color: ${rgba(cFFFFFF, 0.5)};
  background-color: ${c07071D};
  font-family: unset;
`;

const SpreadRow = styled(BaseRow)`
  height: 33px;
  border-top: 1px solid ${mainGridGapColor};
  border-bottom: 1px solid ${mainGridGapColor};
  background-color: ${c07071D};
  margin: 3px 0;

  color: ${rgba(cFFFFFF, 0.5)};
  & :nth-child(2) {
    color: ${rgba(cFFFFFF, 0.7)};
  }
`;

const Row = styled(BaseRow).attrs({ role: 'row' })<{
  readonly clickable: boolean;
}>`
  isolation: isolate; // prevents the volume indication from being clipped by background color by creating a stacking context
  position: relative;
  ${({ clickable }) =>
    clickable &&
    `
      &:hover {
        cursor: pointer;
        background: ${c14142E};
      }
    `}
`;

const Price = styled.span<{ kind: string }>`
  color: ${({ kind }) => (kind === 'bid' ? c00EE91 : cFF005C)};
`;

const Error = styled.div.attrs({ role: 'alert' })`
  height: 100%;
  display: grid;
  place-items: center;
  text-align: center;
  color: ${rgba(cFF005C, 0.7)};
  padding: 16px;
`;
